import { useState } from 'react'
import { X, ArrowLeft, ArrowRight, Check } from 'lucide-react'
import { CampaignType, TimingConfig, Template } from '@/types'
import { useMailboxes } from '@hooks/useMailboxes'
import { useTemplates } from '@hooks/useTemplates'
import { useCreateCampaign } from '@hooks/useCampaigns'
import { toast } from 'react-hot-toast'

interface CreateCampaignDialogProps {
  isOpen: boolean
  onClose: () => void
  onSuccess?: () => void
}

interface CampaignFormData {
  name: string
  mailboxId: number | null
  campaignType: CampaignType | null
  customEmailCount: number  // For custom campaigns: how many emails (1-5)
  template1Id: number | null
  template2Id: number | null
  template3Id: number | null
  template4Id: number | null
  template5Id: number | null
  timingConfig: TimingConfig
}

const CAMPAIGN_TYPES = [
  {
    type: 'WELCOME_SERIES' as CampaignType,
    name: 'Welcome Series',
    description: 'Onboard new customers with a 3-email sequence',
    emails: 3,
    defaultTiming: { day1: 0, day2: 3, day3: 7, sendTime: '09:00' },
    icon: '👋',
  },
  {
    type: 'RE_ENGAGEMENT' as CampaignType,
    name: 'Re-engagement',
    description: 'Win back dormant clients with 4 targeted emails',
    emails: 4,
    defaultTiming: { day1: 0, day2: 7, day3: 14, day4: 21, sendTime: '10:00' },
    icon: '🔄',
  },
  {
    type: 'NEWSLETTER' as CampaignType,
    name: 'Newsletter',
    description: 'Regular updates and content blast to your audience',
    emails: 1,
    defaultTiming: { day1: 0, sendTime: '08:00' },
    icon: '📧',
  },
  {
    type: 'PRODUCT_LAUNCH' as CampaignType,
    name: 'Product Launch',
    description: 'Build anticipation with a 5-email launch sequence',
    emails: 5,
    defaultTiming: { day1: 0, day2: 2, day3: 5, day4: 10, day5: 15, sendTime: '09:30' },
    icon: '🚀',
  },
  {
    type: 'EVENT_PROMOTION' as CampaignType,
    name: 'Event Promotion',
    description: 'Drive event registrations with 3 reminder emails',
    emails: 3,
    defaultTiming: { day1: 0, day2: 3, day3: 7, sendTime: '11:00' },
    icon: '🎪',
  },
  {
    type: 'CUSTOM' as CampaignType,
    name: 'Custom Campaign',
    description: 'Create your own campaign with flexible email count and timing',
    emails: 0, // Variable
    defaultTiming: { day1: 0, sendTime: '09:00' },
    icon: '⚙️',
  },
]

export function CreateCampaignDialog({ isOpen, onClose, onSuccess }: CreateCampaignDialogProps) {
  const [step, setStep] = useState(1)
  const [formData, setFormData] = useState<CampaignFormData>({
    name: '',
    mailboxId: null,
    campaignType: null,
    customEmailCount: 3,  // Default to 3 emails for custom campaigns
    template1Id: null,
    template2Id: null,
    template3Id: null,
    template4Id: null,
    template5Id: null,
    timingConfig: {},
  })

  const { data: mailboxesData } = useMailboxes()
  const mailboxes = mailboxesData?.data || []
  const { data: templatesData } = useTemplates()
  const templates = templatesData?.data || []
  const createCampaign = useCreateCampaign()

  const selectedCampaignType = CAMPAIGN_TYPES.find((ct) => ct.type === formData.campaignType)
  // For custom campaigns, use customEmailCount; otherwise use the predefined count
  const emailCount = formData.campaignType === 'CUSTOM'
    ? formData.customEmailCount
    : (selectedCampaignType?.emails || 0)

  const handleClose = () => {
    setStep(1)
    setFormData({
      name: '',
      mailboxId: null,
      campaignType: null,
      customEmailCount: 3,
      template1Id: null,
      template2Id: null,
      template3Id: null,
      template4Id: null,
      template5Id: null,
      timingConfig: {},
    })
    onClose()
  }

  const handleNext = () => {
    if (step === 1 && !formData.campaignType) {
      toast.error('Please select a campaign type')
      return
    }

    // Always require at least the first template
    if (step === 2 && !formData.template1Id) {
      toast.error('Please select at least the first email template')
      return
    }

    if (step === 3 && !formData.name.trim()) {
      toast.error('Please enter a campaign name')
      return
    }
    if (step === 3 && !formData.mailboxId) {
      toast.error('Please select a mailbox')
      return
    }
    setStep(step + 1)
  }

  const handleBack = () => {
    setStep(step - 1)
  }

  const handleSubmit = async () => {
    // Basic validation
    if (!formData.campaignType || !formData.mailboxId) {
      toast.error('Please complete all required fields')
      return
    }

    // Template validation - always require at least one template
    if (!formData.template1Id) {
      toast.error('Please select at least one template')
      return
    }

    try {
      await createCampaign.mutateAsync({
        name: formData.name,
        mailboxId: formData.mailboxId!,
        campaignType: formData.campaignType ?? undefined,
        template1Id: formData.template1Id ?? undefined,
        template2Id: formData.template2Id ?? undefined,
        template3Id: formData.template3Id ?? undefined,
        template4Id: formData.template4Id ?? undefined,
        template5Id: formData.template5Id ?? undefined,
        timingConfig: Object.keys(formData.timingConfig).length > 0 ? formData.timingConfig : undefined,
      })

      toast.success('Campaign created successfully')
      handleClose()
      onSuccess?.()
    } catch (error: any) {
      toast.error(error?.message || 'Failed to create campaign')
    }
  }

  if (!isOpen) return null

  return (
    <div className="fixed inset-0 z-50 flex items-center justify-center bg-black/70 p-4">
      <div className="bg-slate-900 rounded-2xl shadow-2xl w-full max-w-4xl max-h-[90vh] overflow-hidden flex flex-col border border-slate-700">
        {/* Header */}
        <div className="px-6 py-4 border-b border-slate-700 flex items-center justify-between">
          <h2 className="text-xl font-semibold text-slate-100">Create New Campaign</h2>
          <button
            onClick={handleClose}
            className="text-slate-400 hover:text-slate-200 transition-colors p-2 rounded-lg hover:bg-slate-800"
          >
            <X className="w-5 h-5" />
          </button>
        </div>

        {/* Progress Steps */}
        <div className="px-6 py-4 border-b border-slate-700 bg-slate-800/50">
          <div className="flex items-center justify-between max-w-2xl mx-auto">
            {[1, 2, 3, 4].map((stepNumber) => (
              <div key={stepNumber} className="flex items-center">
                <div
                  className={`w-8 h-8 rounded-full flex items-center justify-center text-sm font-medium transition-colors ${
                    step > stepNumber
                      ? 'bg-emerald-600 text-white'
                      : step === stepNumber
                      ? 'bg-cyan-600 text-white'
                      : 'bg-slate-700 text-slate-400'
                  }`}
                >
                  {step > stepNumber ? <Check className="w-4 h-4" /> : stepNumber}
                </div>
                {stepNumber < 4 && (
                  <div
                    className={`w-16 sm:w-24 h-1 mx-2 transition-colors ${
                      step > stepNumber ? 'bg-emerald-600' : 'bg-slate-700'
                    }`}
                  />
                )}
              </div>
            ))}
          </div>
          <div className="flex items-center justify-between max-w-2xl mx-auto mt-2">
            <span className={`text-xs ${step === 1 ? 'text-cyan-400 font-medium' : 'text-slate-500'}`}>
              Type
            </span>
            <span className={`text-xs ${step === 2 ? 'text-cyan-400 font-medium' : 'text-slate-500'}`}>
              Templates
            </span>
            <span className={`text-xs ${step === 3 ? 'text-cyan-400 font-medium' : 'text-slate-500'}`}>
              Details
            </span>
            <span className={`text-xs ${step === 4 ? 'text-cyan-400 font-medium' : 'text-slate-500'}`}>
              Review
            </span>
          </div>
        </div>

        {/* Content */}
        <div className="flex-1 overflow-y-auto px-6 py-6">
          {/* Step 1: Campaign Type Selection */}
          {step === 1 && (
            <div className="space-y-4">
              <h3 className="text-lg font-medium text-slate-100 mb-4">Select Campaign Type</h3>
              <div className="grid grid-cols-1 sm:grid-cols-2 gap-4">
                {CAMPAIGN_TYPES.map((campaignType) => (
                  <button
                    key={campaignType.type}
                    onClick={() =>
                      setFormData({
                        ...formData,
                        campaignType: campaignType.type,
                        timingConfig: campaignType.defaultTiming,
                      })
                    }
                    className={`p-4 border-2 rounded-xl text-left transition-all hover:shadow-lg ${
                      formData.campaignType === campaignType.type
                        ? 'border-cyan-500 bg-cyan-500/10'
                        : 'border-slate-700 hover:border-cyan-500/50 bg-slate-800/50'
                    }`}
                  >
                    <div className="flex items-start space-x-3">
                      <span className="text-3xl">{campaignType.icon}</span>
                      <div className="flex-1">
                        <h4 className="font-semibold text-slate-100">{campaignType.name}</h4>
                        <p className="text-sm text-slate-400 mt-1">{campaignType.description}</p>
                        <p className="text-xs text-slate-500 mt-2">
                          {campaignType.emails > 0
                            ? `${campaignType.emails} email${campaignType.emails > 1 ? 's' : ''}`
                            : 'Flexible email count'}
                        </p>
                      </div>
                    </div>
                  </button>
                ))}
              </div>
            </div>
          )}

          {/* Step 2: Template Selection */}
          {step === 2 && (
            <div className="space-y-4">
              <h3 className="text-lg font-medium text-slate-100 mb-4">Select Email Templates</h3>

              {/* Custom campaign: first select number of emails */}
              {formData.campaignType === 'CUSTOM' && (
                <div className="bg-cyan-500/10 border border-cyan-500/20 rounded-xl p-4 mb-6">
                  <label className="block text-sm font-medium text-slate-200 mb-2">
                    How many emails in this sequence?
                  </label>
                  <div className="flex items-center gap-4">
                    {[1, 2, 3, 4, 5].map((num) => (
                      <button
                        key={num}
                        type="button"
                        onClick={() => setFormData({ ...formData, customEmailCount: num })}
                        className={`w-12 h-12 rounded-lg font-semibold text-lg transition-colors ${
                          formData.customEmailCount === num
                            ? 'bg-cyan-600 text-white'
                            : 'bg-slate-800 border border-slate-600 text-slate-300 hover:border-cyan-500'
                        }`}
                      >
                        {num}
                      </button>
                    ))}
                  </div>
                </div>
              )}

              {/* Template selection for all campaign types */}
              <div className="space-y-4">
                <p className="text-sm text-slate-400 mb-6">
                  {formData.campaignType === 'CUSTOM'
                    ? `Select templates for your ${emailCount}-email sequence`
                    : `${selectedCampaignType?.name} requires ${emailCount} email${emailCount > 1 ? 's' : ''}`}
                </p>

                {Array.from({ length: emailCount }, (_, i) => i + 1).map((emailNum) => (
                  <div key={emailNum} className="space-y-2">
                    <label className="block text-sm font-medium text-slate-300">
                      Email {emailNum} Template{emailNum === 1 ? ' (Required)' : ''}
                    </label>
                    <select
                      value={
                        emailNum === 1
                          ? formData.template1Id || ''
                          : emailNum === 2
                          ? formData.template2Id || ''
                          : emailNum === 3
                          ? formData.template3Id || ''
                          : emailNum === 4
                          ? formData.template4Id || ''
                          : formData.template5Id || ''
                      }
                      onChange={(e) => {
                        const value = e.target.value ? Number(e.target.value) : null
                        setFormData({
                          ...formData,
                          ...(emailNum === 1 && { template1Id: value }),
                          ...(emailNum === 2 && { template2Id: value }),
                          ...(emailNum === 3 && { template3Id: value }),
                          ...(emailNum === 4 && { template4Id: value }),
                          ...(emailNum === 5 && { template5Id: value }),
                        })
                      }}
                      className="w-full px-3 py-2 bg-slate-800 border border-slate-600 rounded-xl text-slate-200 focus:outline-none focus:ring-2 focus:ring-cyan-500 focus:border-transparent"
                    >
                      <option value="">Select a template...</option>
                      {templates.map((template: Template) => (
                        <option key={template.id} value={template.id}>
                          {template.name} - {template.subject}
                        </option>
                      ))}
                    </select>
                  </div>
                ))}
              </div>
            </div>
          )}

          {/* Step 3: Campaign Details & Timing */}
          {step === 3 && (
            <div className="space-y-6">
              <h3 className="text-lg font-medium text-slate-100 mb-4">Campaign Details</h3>

              {/* Campaign Name */}
              <div className="space-y-2">
                <label className="block text-sm font-medium text-slate-300">Campaign Name</label>
                <input
                  type="text"
                  value={formData.name}
                  onChange={(e) => setFormData({ ...formData, name: e.target.value })}
                  placeholder="Enter campaign name..."
                  className="input-modern"
                />
              </div>

              {/* Mailbox Selection */}
              <div className="space-y-2">
                <label className="block text-sm font-medium text-slate-300">Sending Mailbox</label>
                <select
                  value={formData.mailboxId || ''}
                  onChange={(e) =>
                    setFormData({ ...formData, mailboxId: e.target.value ? Number(e.target.value) : null })
                  }
                  className="w-full px-3 py-2 bg-slate-800 border border-slate-600 rounded-xl text-slate-200 focus:outline-none focus:ring-2 focus:ring-cyan-500 focus:border-transparent"
                >
                  <option value="">Select a mailbox...</option>
                  {mailboxes
                    ?.filter((mb) => mb.isActive)
                    .map((mailbox) => (
                      <option key={mailbox.id} value={mailbox.id}>
                        {mailbox.name} ({mailbox.email})
                      </option>
                    ))}
                </select>
              </div>

              {/* Timing Configuration */}
              <div className="space-y-4 border-t border-slate-700 pt-6">
                <h4 className="text-md font-medium text-slate-100">Email Timing</h4>
                <p className="text-sm text-slate-400">Configure when each email should be sent</p>

                <div className="space-y-3">
                  {Array.from({ length: emailCount }, (_, i) => i + 1).map((emailNum) => (
                    <div key={emailNum} className="flex items-center space-x-4">
                      <label className="text-sm font-medium text-slate-300 w-24">Email {emailNum}:</label>
                      <div className="flex items-center space-x-2 flex-1">
                        <span className="text-sm text-slate-400">Day</span>
                        <input
                          type="number"
                          min="0"
                          max="365"
                          value={
                            emailNum === 1
                              ? formData.timingConfig.day1 ?? 0
                              : emailNum === 2
                              ? formData.timingConfig.day2 ?? 3
                              : emailNum === 3
                              ? formData.timingConfig.day3 ?? 7
                              : emailNum === 4
                              ? formData.timingConfig.day4 ?? 14
                              : formData.timingConfig.day5 ?? 21
                          }
                          onChange={(e) => {
                            const value = parseInt(e.target.value)
                            setFormData({
                              ...formData,
                              timingConfig: {
                                ...formData.timingConfig,
                                ...(emailNum === 1 && { day1: value }),
                                ...(emailNum === 2 && { day2: value }),
                                ...(emailNum === 3 && { day3: value }),
                                ...(emailNum === 4 && { day4: value }),
                                ...(emailNum === 5 && { day5: value }),
                              },
                            })
                          }}
                          className="w-20 px-2 py-1 bg-slate-800 border border-slate-600 rounded-lg text-slate-200 focus:outline-none focus:ring-2 focus:ring-cyan-500"
                        />
                      </div>
                    </div>
                  ))}

                  <div className="flex items-center space-x-4 pt-2">
                    <label className="text-sm font-medium text-slate-300 w-24">Send Time:</label>
                    <input
                      type="time"
                      value={formData.timingConfig.sendTime || '09:00'}
                      onChange={(e) =>
                        setFormData({
                          ...formData,
                          timingConfig: { ...formData.timingConfig, sendTime: e.target.value },
                        })
                      }
                      className="px-2 py-1 bg-slate-800 border border-slate-600 rounded-lg text-slate-200 focus:outline-none focus:ring-2 focus:ring-cyan-500"
                    />
                  </div>
                </div>
              </div>
            </div>
          )}

          {/* Step 4: Review */}
          {step === 4 && (
            <div className="space-y-6">
              <h3 className="text-lg font-medium text-slate-100 mb-4">Review Campaign</h3>

              <div className="bg-slate-800/50 rounded-xl p-4 space-y-4 border border-slate-700">
                <div>
                  <span className="text-sm font-medium text-slate-400">Campaign Name:</span>
                  <p className="text-slate-100 mt-1">{formData.name}</p>
                </div>

                <div>
                  <span className="text-sm font-medium text-slate-400">Campaign Type:</span>
                  <p className="text-slate-100 mt-1">
                    {selectedCampaignType?.icon} {selectedCampaignType?.name}
                  </p>
                </div>

                <div>
                  <span className="text-sm font-medium text-slate-400">Mailbox:</span>
                  <p className="text-slate-100 mt-1">
                    {mailboxes?.find((mb) => mb.id === formData.mailboxId)?.email}
                  </p>
                </div>

                <div>
                  <span className="text-sm font-medium text-slate-400">Email Sequence:</span>
                  <ul className="mt-2 space-y-2">
                    {[1, 2, 3, 4, 5].map((num) => {
                      const templateId =
                        num === 1
                          ? formData.template1Id
                          : num === 2
                          ? formData.template2Id
                          : num === 3
                          ? formData.template3Id
                          : num === 4
                          ? formData.template4Id
                          : formData.template5Id
                      if (!templateId) return null

                      const template = templates.find((t: Template) => t.id === templateId)
                      const day =
                        num === 1
                          ? formData.timingConfig.day1 ?? 0
                          : num === 2
                          ? formData.timingConfig.day2
                          : num === 3
                          ? formData.timingConfig.day3
                          : num === 4
                          ? formData.timingConfig.day4
                          : formData.timingConfig.day5

                      return (
                        <li key={num} className="flex items-center justify-between text-sm">
                          <span className="text-slate-400">
                            Email {num} (Day {day}):
                          </span>
                          <span className="text-slate-100 font-medium">{template?.name}</span>
                        </li>
                      )
                    })}
                  </ul>
                </div>

                <div>
                  <span className="text-sm font-medium text-slate-400">Send Time:</span>
                  <p className="text-slate-100 mt-1">{formData.timingConfig.sendTime || '09:00'}</p>
                </div>
              </div>
            </div>
          )}
        </div>

        {/* Footer */}
        <div className="px-6 py-4 border-t border-slate-700 flex items-center justify-between bg-slate-800/50">
          <button
            onClick={step === 1 ? handleClose : handleBack}
            className="px-4 py-2 text-slate-400 hover:text-slate-200 transition-colors flex items-center space-x-2"
            disabled={createCampaign.isPending}
          >
            {step === 1 ? (
              <>
                <X className="w-4 h-4" />
                <span>Cancel</span>
              </>
            ) : (
              <>
                <ArrowLeft className="w-4 h-4" />
                <span>Back</span>
              </>
            )}
          </button>

          <div className="flex items-center space-x-3">
            {step < 4 ? (
              <button
                onClick={handleNext}
                className="btn-primary"
              >
                <span>Next</span>
                <ArrowRight className="w-4 h-4" />
              </button>
            ) : (
              <button
                onClick={handleSubmit}
                disabled={createCampaign.isPending}
                className="px-6 py-2 bg-emerald-600 text-white rounded-xl hover:bg-emerald-700 transition-colors flex items-center space-x-2 disabled:opacity-50 disabled:cursor-not-allowed"
              >
                <Check className="w-4 h-4" />
                <span>{createCampaign.isPending ? 'Creating...' : 'Create Campaign'}</span>
              </button>
            )}
          </div>
        </div>
      </div>
    </div>
  )
}
